import { useEffect, useState } from 'react';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { LISTINGS_SPLIT_FEATURE_NAME, ZRM_LISTING_LIST_ENABLED, getCookieAsBoolean } from '../utils/featureFlags';

export type UseListingsNavEntryParams = {
  loggedIn?: boolean;
  /** Returns this value verbatim and skips both the cookie read and the Split call. */
  override?: boolean;
};

/**
 * Uses the generated ts-client hook rather than `useGetTreatment` from `@zg-rentals/split`
 * because the latter requires `SplitProvider`, which most apps consuming this module do not
 * mount. `isLoading` is for route guards — redirecting before the treatment resolves would
 * bounce every enrolled user.
 */
export const useListingsNavEntry = ({ loggedIn, override }: UseListingsNavEntryParams = {}) => {
  // Read after mount: during render the cookie is false on the server, mismatching on hydration.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useEffect(() => {
    setCookie({ checked: true, enabled: getCookieAsBoolean(ZRM_LISTING_LIST_ENABLED) });
  }, []);

  // `!cookie.enabled` keeps QA opt-ins out of the experiment's exposure data.
  const isQueryEnabled = override === undefined && Boolean(loggedIn) && cookie.checked && !cookie.enabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: LISTINGS_SPLIT_FEATURE_NAME },
    {
      enabled: isQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['listings-split-treatment'],
        context: { api: 'getTreatment', details: { featureName: LISTINGS_SPLIT_FEATURE_NAME } },
      },
    },
  );

  if (override !== undefined) {
    return { isListingsNavEnabled: override, isLoading: false };
  }

  return {
    isListingsNavEnabled: cookie.enabled || data?.treatment === 'on',
    isLoading: Boolean(loggedIn) && (!cookie.checked || (isQueryEnabled && isPending)),
  };
};

export default useListingsNavEntry;
